import React, { useState } from 'react'
import { useSelector, useDispatch, batch } from 'react-redux'
import { Select, Table, Tag } from 'antd'
import { CloseOutlined } from '@ant-design/icons';

// Constants
import { TEMPLATE } from '../../../../../constants'

// Components
import { PointType } from '../TransitRecordsDrawer/micro/PointTimeline';
// import { PointType } from './micro/PointTimeline'

// Handlers
import { vehicles_handler } from '../../../../../handlers'

// Redux Action
import * as ACTIONS from '../../../../../services/redux/actions'

// Services
import { TIME } from '../../../../../services/util'
import { getAddressHDLR, getLocAndVIDHDLR } from '../../../../../handlers/address';
import { eventNameTranslator } from '../../../../../handlers/events';

const { parseTime } = TIME
const { locationMarkiser } = vehicles_handler

const {
    EVENT_TICKET_TYPES_BY_CODES,
    EVENT_TICKET_TYPES_OBJECT,
    FILTERABLE_EVENT_TYPE,
    FILTERABLE_VEHICLE_TRANSIT_STATUS,
    TRANSIT_AND_EVENT_DURATIONS_OBJECT,
    TRANSIT_AND_EVENT_DURATIONS_TYPE,
    TRIP_RECORD_EVENT_ICON,
    TRANSIT_STATUS_OBJECT,
} = TEMPLATE.STATUS

const STATUS_COLOR = {
    INFO:     { color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
    WARNING:  { color: '#d46b08', bg: '#fff7e6', border: '#ffd591' },
    ALERT:    { color: '#cf1322', bg: '#fff1f0', border: '#ffa39e' },
    CRITICAL: { color: '#cf1322', bg: '#fff1f0', border: '#ffa39e' },
}

const TRANSIT_DOT = {
    [TRANSIT_STATUS_OBJECT?.MOVING]:       '#52c41a',
    [TRANSIT_STATUS_OBJECT?.IDLING]:       '#faad14',
    [TRANSIT_STATUS_OBJECT?.PARKING]:      '#1677ff',
    [TRANSIT_STATUS_OBJECT?.DISCONNECTED]: '#8c8c8c',
}

const TransitRecordsDrawer2 = ({ mapRef }) => {

    const dispatch = useDispatch()

    const [eventTypeFilters, setEventTypeFilters] = useState(FILTERABLE_EVENT_TYPE)
    const [transitTypeFilters, setTransitTypeFilters] = useState(FILTERABLE_VEHICLE_TRANSIT_STATUS)
    const [transitAndEventDurationFilterKey, setTransitAndEventDurationFilterKey] = useState(TRANSIT_AND_EVENT_DURATIONS_OBJECT.ALL)
    const [selectedRowKey, setSelectedRowKey] = useState(null)

    const vid = useSelector(state => state.containers.v3.vehicles.selectedID)
    const vehicle = useSelector(state => state.containers.v3.vehicles.staticsByID[vid] || {})
    const vehicleTransitRecords = useSelector(state => (vid && state.containers.v3.transitRecords.recordByVID[vid]) || [])
    const vehicleEventLogs = useSelector(state => (vid && state.containers.v3.vehicleEventLogs.logByVID[vid]) || [])
    const rules = useSelector(state => state.containers.v3.rules.byID)
    const coordinates = useSelector(state => state.containers.v3.locationTracking.byCoordinates)
    const vehicleTripSelectedStartTime = useSelector(state => state.containers.v3.tripRecords.selectedStartTime)
    const vehicleTripSelectedEndTime = useSelector(state => state.containers.v3.tripRecords.selectedEndTime)

    const vehicleSelectedTransitRecords =
        vehicleTransitRecords
            .filter(tr => vehicleTripSelectedStartTime <= tr.startTime && vehicleTripSelectedEndTime >= tr.endTime)
            .filter(tr => transitTypeFilters.includes(tr.transitStatus))
            .filter(tr => {
                const duration = TRANSIT_AND_EVENT_DURATIONS_TYPE[transitAndEventDurationFilterKey]
                if (!duration) return true
                return tr.duration >= duration.value
            })
            .map((tr, index) => ({
                ...tr,
                key: tr.tsid || `ts#${index}`,
                generatedAt: tr.startTime,
                type: PointType.TRANSIT_RECORD.label,
            }))

    const vehicleSelectedEventLogs =
        vehicleEventLogs
            .filter(e => vehicleTripSelectedStartTime <= e.vehicleTime && vehicleTripSelectedEndTime >= e.vehicleTime)
            .map(e => ({ ...e, ...rules[e.eventid] }))
            .filter(e => eventTypeFilters.includes(EVENT_TICKET_TYPES_BY_CODES[e.type_rule]))
            .map((e, index) => ({
                ...e.rule,
                vid: e.vid,
                location: e.location,
                eventid: e.eventid,
                mdvrid: vehicle.mdvrid,
                name: e.rule?.name,
                key: `${e.eventTime}#${index}` || `event#${index}`,
                generatedAt: e.vehicleTime,
                type: PointType.EVENT_LOG.label,
                attachments: e.attachments?.length ? e.attachments : null,
                statusTag: EVENT_TICKET_TYPES_BY_CODES[e.type_rule],
            }))
        
    const telemetryPoints = [...vehicleSelectedTransitRecords, ...vehicleSelectedEventLogs]
        .sort((a, b) => a.generatedAt - b.generatedAt)
        .map(tp => ({
            ...tp,
            start_address: coordinates[`${tp.start_loc?.lat || tp.location?.lat},${tp.start_loc?.lon || tp.location?.lon}`] || '-'
        }))

    const handleRowClick = (record) => {
        const { key, type } = record
        const point = telemetryPoints.find(p => p.key === key)

        if (mapRef && (point.start_loc?.lat || point.location?.lat)) {
            mapRef.panTo(locationMarkiser(point.start_loc || point.location))
            mapRef.setZoom(18)
        }

        if (point) {
            switch (type) {
                case PointType.EVENT_LOG.label: {
                    dispatch(ACTIONS.v3_mapControl.focus_on_map_detail_point_request())
                    dispatch(ACTIONS.v3_mapControl.add_enabled_event_pop_up(`${point.eventid}#${point.generatedAt}`))
                    break
                }
                case PointType.TRANSIT_RECORD.label: {
                    batch(() => {
                        dispatch(ACTIONS.v3_mapControl.focus_on_map_detail_point_request())
                        dispatch(ACTIONS.v3_transitRecords.set_selected_transit_record_start_time_request(point.startTime))
                        dispatch(ACTIONS.v3_transitRecords.set_selected_transit_record_end_time_request(point.endTime))
                    })
                    break
                }
                default: break
            }
        }

        setSelectedRowKey(key)
    }

    getAddressHDLR(getLocAndVIDHDLR([...vehicleTransitRecords, ...vehicleEventLogs]))

    // ── columns ───────────────────────────────────────────────────────────────

    const columns = [
        {
            title: 'Time',
            dataIndex: 'generatedAt',
            key: 'time',
            width: 160,
            render: (val) => (
                <span style={{ fontSize: 13, color: '#444', whiteSpace: 'nowrap' }}>
                    {parseTime(val)}
                </span>
            ),
        },
        {
            title: 'Event',
            key: 'event',
            width: 260,
            render: (_, record) => {
                const isTransit = record.type === PointType.TRANSIT_RECORD.label
                const dotColor  = isTransit
                    ? (TRANSIT_DOT[record.transitStatus] || '#8c8c8c')
                    : '#8c8c8c'

                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isTransit ? (
                            <span style={{
                                width: 10, height: 10, borderRadius: '50%',
                                backgroundColor: dotColor, flexShrink: 0,
                                display: 'inline-block'
                            }} />
                        ) : (
                            <img
                                src={TRIP_RECORD_EVENT_ICON[record.statusTag] || TRIP_RECORD_EVENT_ICON[EVENT_TICKET_TYPES_OBJECT.INFO]}
                                alt=""
                                style={{ width: 16, height: 16, flexShrink: 0 }}
                            />
                        )}
                        <span style={{ fontSize: 13 }}>{eventNameTranslator(record) || record.transitStatus}</span>
                    </span>
                )
            },
        },
        {
            title: 'Location',
            key: 'location',
            render: (data) => {
                return <span style={{ fontSize: 13, color: '#444' }}>
                    {data.address || data.start_address}
                </span>
            }
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, record) => {
                if (record.type === PointType.TRANSIT_RECORD.label) return <span style={{ color: '#bbb' }}>-</span>
                const tag = record.statusTag
                const s   = STATUS_COLOR[tag]
                if (!s) return <span style={{ color: '#bbb' }}>-</span>
                return (
                    <Tag style={{
                        color: s.color,
                        background: s.bg,
                        border: `1px solid ${s.border}`,
                        borderRadius: 4,
                        fontWeight: 500,
                        fontSize: 12,
                    }}>
                        {tag}
                    </Tag>
                )
            },
        },
    ]

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 99,
            backgroundColor: 'white',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '45vh',
        }}>
            {/* ── Header row ── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px 6px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
            }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>Trip Playback Events</span>

                {/* Filters inline */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, marginLeft: 24 }}>
                    <Select
                        size="small"
                        defaultValue={transitAndEventDurationFilterKey}
                        onChange={setTransitAndEventDurationFilterKey}
                        style={{ minWidth: 100 }}
                        placeholder="Duration"
                    >
                        {Object.keys(TRANSIT_AND_EVENT_DURATIONS_OBJECT).map(key => {
                            const dur = TRANSIT_AND_EVENT_DURATIONS_TYPE[key]
                            return <Select.Option key={key}>{dur.label}</Select.Option>
                        })}
                    </Select>

                    <Select
                        size="small"
                        mode="multiple"
                        defaultValue={eventTypeFilters}
                        onChange={setEventTypeFilters}
                        style={{ minWidth: 180 }}
                        placeholder="Event Types"
                        maxTagCount="responsive"
                    >
                        {FILTERABLE_EVENT_TYPE.map(et => (
                            <Select.Option key={et}>
                                <img
                                    src={TRIP_RECORD_EVENT_ICON[et] || TRIP_RECORD_EVENT_ICON[EVENT_TICKET_TYPES_OBJECT.INFO]}
                                    alt=""
                                    style={{ width: 14, height: 14, marginRight: 6 }}
                                />
                                {et}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        mode="multiple"
                        defaultValue={transitTypeFilters}
                        onChange={setTransitTypeFilters}
                        style={{ minWidth: 180 }}
                        placeholder="Transit Types"
                        maxTagCount="responsive"
                    >
                        {FILTERABLE_VEHICLE_TRANSIT_STATUS.map(tt => (
                            <Select.Option key={tt}>{tt}</Select.Option>
                        ))}
                    </Select>
                </div>

                <CloseOutlined
                    onClick={() => {
                        setTransitAndEventDurationFilterKey(null)
                        dispatch(ACTIONS.v3_mapControl.close_transit_record_drawer_on_map_request())
                    }}
                    style={{ marginLeft: 16, cursor: 'pointer', color: '#595959' }}
                />
            </div>

            {/* ── Table ── */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                <Table
                    size="small"
                    columns={columns}
                    dataSource={telemetryPoints.filter(p => p.type)}
                    pagination={false}
                    rowKey="key"
                    scroll={{ y: 'calc(45vh - 58px)' }}
                    onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: {
                            cursor: 'pointer',
                            backgroundColor: record.key === selectedRowKey ? '#e6f4ff' : undefined,
                        }
                    })}
                />
            </div>
        </div>
    )
}

export default TransitRecordsDrawer2